import {
  brushSizeMode,
  canonicalVoxelType,
  digScope,
  forceStraightScribble,
  maxBrushRadius,
  placementPolicy,
  sameTypeExpansionRadius,
  strokePolicy,
  usesSurfaceOrientation,
  type DigScope,
} from '@/voxels/registry'
import { tintStrokeNeverOverwrites } from '@/voxels/tint/tintConstants'
import { normalizeKeepTypes, type BrushVerb } from '@/brush/rasterize'

export type BrushPolicy = {
  swept: boolean
  verb: BrushVerb
  /** Canonical type the stroke is scoped to (verbs `subtractIf` / `repaintIf`); absent otherwise. */
  scopeType?: string
}

export type BrushPolicyOpts = {
  /** Resolved eraser scope for this stroke (see `resolveDigScope`). */
  digScope?: DigScope
  /** Type of the voxel under the stroke's first hit, when the stroke started on a voxel. */
  startHitType?: string
}

/** Minimum brush radius (voxels) for the swept path; below it the pencil/legacy spheres are kept. */
export const SWEPT_BRUSH_MIN_RADIUS = 2

/**
 * The radius a stroke of `type` actually uses: the UI radius clamped to the type's `maxRadius`
 * (B2). Both the controller (stroke start) and the hover preview must go through this so the ghost,
 * the preview stamp and the committed stroke agree. `maxRadius` bounds a single stroke only; it is
 * no protection against accumulated group size (plan §6).
 */
export function effectiveBrushRadius(type: string | undefined, uiRadius: number | undefined): number {
  const r = Math.max(0, uiRadius ?? 0)
  if (!type) return r
  return Math.min(r, maxBrushRadius(type))
}

/**
 * Eraser scope for a stroke (B5, Q11): the explicit eraser option wins; otherwise the registry
 * `digScope` of the type under the first hit (bulk types 'self', everything else 'any'). A stroke
 * that starts off any voxel erases anything.
 */
export function resolveDigScope(explicit: DigScope | undefined, startHitType: string | undefined): DigScope {
  if (explicit === 'self' || explicit === 'any') return explicit
  return startHitType ? digScope(startHitType) : 'any'
}

/**
 * The verb a swept draw stroke writes once the stroke's own options apply: `fillOnly`
 * (ScribbleSettings) turns 'place' into 'fill', so the stroke writes empty cells only. Scoped and
 * erase verbs are unchanged. Shared by the controller and the hover preview.
 */
export function strokeVerbWithOptions(verb: BrushVerb, settings: { fillOnly?: boolean; level?: number; coat?: { thickness: number } } | undefined): BrushVerb {
  if (settings?.level && settings.level > 0 && (verb === 'place' || verb === 'fill')) return 'level'
  if (settings?.coat && (verb === 'place' || verb === 'fill')) return 'coat'
  return settings?.fillOnly && verb === 'place' ? 'fill' : verb
}

/** The eraser options that only the swept brush implements (`EraserSettings.protect`, `redig`). */
export type EraserSweptOptions = { protect?: readonly string[]; redig?: boolean; floorY?: number }

/** The eraser's protected types in snapshot form (`BrushSnapshot.keep`), or undefined when none. */
export function eraserKeepTypes(eraser: EraserSweptOptions | undefined): string[] | undefined {
  return normalizeKeepTypes(eraser?.protect)
}

/**
 * An eraser with protected types or re-digging must take the swept path whatever the brush flag and
 * the radius say: only the swept fold knows those rules. (Like the nib, a swept-only option.)
 */
export function eraserForcesSwept(eraser: EraserSweptOptions | undefined): boolean {
  return !!eraserKeepTypes(eraser) || !!eraser?.redig || eraserFloorY(eraser) !== undefined
}

/** The eraser's floor in snapshot form (`BrushSnapshot.floorY`), or undefined when it has none. */
export function eraserFloorY(eraser: EraserSweptOptions | undefined): number | undefined {
  const y = eraser?.floorY
  return y !== undefined && Number.isInteger(y) && y > 0 ? y : undefined
}

/**
 * Whether strokes of `type` may use the swept (capsule) brush, and which verb they write with.
 * Swept applies only to faceless, volume-brush, non-surface types that take the generic sphere
 * expansion (no same-type accretion, no forced straight strokes), and only when the radius is at
 * least SWEPT_BRUSH_MIN_RADIUS.
 *
 * Verbs: erasing writes `subtract`, or `subtractIf` scoped to the first-hit type when the resolved
 * dig scope is 'self' (bulk materials dig only themselves). Drawing writes the type's `stroke.verb`
 * ('place' by default, 'fill' for poured materials), or `repaintIf` (smear) when the type opts into
 * `smearOnSameType` and the stroke starts on a cell of the same type.
 */
export function brushPolicy(type: string, radius?: number, subtract = false, opts?: BrushPolicyOpts): BrushPolicy {
  const swept = radius == null || radius >= SWEPT_BRUSH_MIN_RADIUS
  if (subtract) {
    const scoped = opts?.digScope === 'self' && !!opts.startHitType
    return scoped
      ? { swept, verb: 'subtractIf', scopeType: canonicalVoxelType(opts!.startHitType!) }
      : { swept, verb: 'subtract' }
  }
  const stroke = strokePolicy(type)
  const canonical = canonicalVoxelType(type)
  const smear = !!stroke?.smearOnSameType
    && !!opts?.startHitType
    && canonicalVoxelType(opts.startHitType) === canonical
  // Tint never overwrites (tintStrokeNeverOverwrites).
  const verb: BrushVerb = smear ? 'repaintIf' : tintStrokeNeverOverwrites(canonical) ? 'fill' : (stroke?.verb ?? 'place')
  const sweptType = !usesSurfaceOrientation(type)
    && brushSizeMode(type) === 'volume'
    && placementPolicy(type).target !== 'surface'
    && sameTypeExpansionRadius(type) == null
    && !forceStraightScribble(type)
  return smear
    ? { swept: swept && sweptType, verb, scopeType: canonical }
    : { swept: swept && sweptType, verb }
}
